// src/context/AuthContext.tsx
/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { supabase, authService, communityService } from '../lib/supabase.ts';
import { clearChatHistory } from '../lib/services/ai/aiChatService.ts';
import type { UserProfile, AuthContextType } from '../types/index.ts';
import type { User } from '@supabase/supabase-js';
import { identifyUser, resetUser } from '../lib/posthog';
import { setSentryUser, clearSentryUser } from '../lib/sentry';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [authModalMode, setAuthModalMode] = useState<'signin' | 'signup' | 'reset' | 'newpassword' | null>(null);
  const showAuthModalRef = useRef(showAuthModal);
  const authModalModeRef = useRef(authModalMode);

  useEffect(() => {
    showAuthModalRef.current = showAuthModal;
  }, [showAuthModal]);

  useEffect(() => {
    authModalModeRef.current = authModalMode;
  }, [authModalMode]);

  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const user = await authService.getCurrentUser();
        setUser(user);
        if (user) {
          try {
            const prof = await communityService.getProfile(user.id);
            setProfile(prof);
          } catch (err) {
            console.error('Error fetching profile:', err);
          }
        }
      } catch (err) {
        console.error('Error getting current user:', err);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const unsubscribe = authService.onAuthStateChange((event, session) => {
      const user = session?.user ?? null;
      setUser(user);
      if (user) {
        identifyUser(user.id);
        setSentryUser(user.id);
        communityService
          .getProfile(user.id)
          .then(setProfile)
          .catch(err => console.error('Error fetching profile:', err));
      } else {
        resetUser();
        clearSentryUser();
        setProfile(null);
      }
      setLoading(false);

      // Handle password recovery flow
      if (event === 'PASSWORD_RECOVERY') {
        setAuthModalMode('newpassword');
        setShowAuthModal(true);
        return;
      }

      // Close modal when user successfully logs in (but not during recovery)
      if (user && showAuthModalRef.current && authModalModeRef.current !== 'newpassword') {
        setShowAuthModal(false);
      }
    });

    return unsubscribe;
  }, []);

  const openAuthModal = useCallback(
    (mode?: 'signin' | 'signup' | 'reset' | 'newpassword'): void => {
      setAuthModalMode(mode ?? null);
      setShowAuthModal(true);
    },
    []
  );
  const closeAuthModal = useCallback((): void => {
    setShowAuthModal(false);
    setAuthModalMode(null);
  }, []);

  const signUp = useCallback(async (email: string, password: string, profileInput: Record<string, unknown> = {}): Promise<{ user: User | null; session: unknown }> => {
    const data = await authService.signUp(email, password, profileInput);
    if (data?.user?.email_confirmed_at) {
      try {
        const prof = await communityService.getProfile(data.user.id);
        setProfile(prof);
      } catch {
        console.warn('Profile not available yet (user needs email verification)');
      }
    }

    // Trigger welcome email sequence (fire-and-forget, authenticated)
    if (data?.user) {
      supabase.auth.getSession().then(({ data: sessionData }) => {
        const token = sessionData?.session?.access_token;
        fetch(new URL('/api/email/trigger-welcome', window.location.origin).toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            email,
            firstName: (profileInput.display_name as string) || (profileInput.username as string) || email.split('@')[0],
          }),
        }).catch((err) => console.warn('Welcome email trigger failed (non-critical):', err));
      });
    }

    return data;
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<{ user: User | null; session: unknown }> => {
    const data = await authService.signIn(email, password);
    if (data?.user) {
      const prof = await communityService.getProfile(data.user.id);
      setProfile(prof);
    }
    return data;
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    // Clear Ember chat history on logout for privacy and fresh sessions
    if (user) {
      try {
        await clearChatHistory(user.id);
      } catch (error) {
        console.warn('Failed to clear chat history on logout:', error);
        // Don't block logout if chat clear fails
      }
    }

    await authService.signOut();
    setProfile(null);
  }, [user]);

  const resetPassword = useCallback(async (email: string): Promise<{ error?: Error | null }> => {
    return await authService.resetPassword(email);
  }, []);

  const updatePassword = useCallback(async (newPassword: string): Promise<{ error?: Error | null }> => {
    return await authService.updatePassword(newPassword);
  }, []);

  const updateProfile = useCallback(async (updates: Partial<UserProfile>): Promise<UserProfile> => {
    if (!user) throw new Error('Not authenticated');
    const newProfile = await communityService.updateProfile(user.id, updates);
    setProfile(newProfile);
    return newProfile;
  }, [user]);

  const value = useMemo<AuthContextType>(() => ({
    user,
    profile,
    loading,
    isAuthenticated: Boolean(user),
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    showAuthModal,
    authModalMode,
    openAuthModal,
    closeAuthModal,
  }), [user, profile, loading, signUp, signIn, signOut, resetPassword, updatePassword, updateProfile, showAuthModal, authModalMode, openAuthModal, closeAuthModal]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
